import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HardwareType, ModelReference } from '../../services/typesService';
import { useI18n } from '../../contexts/I18nContext';
import AuditHistoryTab from '../audit/AuditHistoryTab';
import ProductConfigTab from './ProductConfigTab';
import { useHardwareTypeDetail, HardwareTypeTab } from './hardwareTypeDetail/useHardwareTypeDetail';
import { HardwareTypeOverviewTab } from './hardwareTypeDetail/HardwareTypeOverviewTab';
import { HardwareTypeModelRefsTab } from './hardwareTypeDetail/HardwareTypeModelRefsTab';

interface HardwareTypeDetailModalProps {
  hardwareType: HardwareType | null;
  modelRefs: ModelReference[];
  isOpen: boolean;
  canEdit?: boolean;
  onClose: () => void;
  onTypeUpdated: () => void;
}

/**
 * One hardware type: identity and behaviour, its product configuration,
 * its model references, a link to its devices, and its history. The
 * state and the actions live in `useHardwareTypeDetail`.
 */
export const HardwareTypeDetailModal: React.FC<HardwareTypeDetailModalProps> = ({ hardwareType, modelRefs, isOpen, canEdit = false, onClose, onTypeUpdated }) => {
  const navigate = useNavigate();
  const { formatNumber, t } = useI18n();
  const state = useHardwareTypeDetail(hardwareType, modelRefs, isOpen, onTypeUpdated);
  const { activeTab, setActiveTab, isEditing, isLoading, error, successMessage } = state;

  if (!isOpen || !hardwareType) return null;

  const deviceCount = hardwareType.device_count || 0;
  const tabs: Array<{ key: HardwareTypeTab; label: string; testId: string; count?: number }> = [
    { key: 'overview', label: t('tabs.overview'), testId: 'tab-hardware-type-overview' },
    { key: 'config', label: t('deviceTypes.productConfig'), testId: 'tab-hardware-type-config' },
    { key: 'modelrefs', label: t('deviceTypes.modelReferences'), testId: 'tab-hardware-type-modelrefs', count: state.localModelRefs.length },
    { key: 'devices', label: t('deviceTypes.devices'), testId: 'tab-hardware-type-devices', count: deviceCount },
    { key: 'history', label: t('tabs.history'), testId: 'tab-hardware-type-history' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="config-template-detail-modal tpd dtd" data-testid="modal-hardware-type-detail" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title tpd-title">
            <span className="tpd-mark" aria-hidden="true">{hardwareType.code}</span>
            <div className="tpd-title-text">
              <h2>{isEditing ? t('deviceTypes.detail.editTitle') : hardwareType.name}</h2>
              <div className="tpd-title-sub">
                <span>{hardwareType.code}</span>
                {!hardwareType.is_active && <span className="tpd-off">{t('common.inactive')}</span>}
                {hardwareType.is_default && <span className="default-badge">{t('common.default')}</span>}
              </div>
            </div>
          </div>
          <button type="button" className="close-button" data-testid="btn-modal-close" onClick={onClose} aria-label={t('common.close')}>&times;</button>
        </div>

        <div className="modal-tabs" role="tablist">
          {tabs.map((tab) => (
            <button key={tab.key} type="button" role="tab" aria-selected={activeTab === tab.key} className={`tab-button ${activeTab === tab.key ? 'active' : ''}`} data-testid={tab.testId} onClick={() => setActiveTab(tab.key)}>
              {tab.label}{tab.count !== undefined && <span className="ud-tab-count">{formatNumber(tab.count)}</span>}
            </button>
          ))}
        </div>

        <div className="modal-content tpd-body">
          {error && <div className="error-message" role="alert">{error}</div>}
          {successMessage && (
            <div className="success-banner">
              <span>{successMessage}</span>
              <button type="button" onClick={() => state.setSuccessMessage(null)} aria-label={t('common.close')}>&times;</button>
            </div>
          )}

          {activeTab === 'overview' && <HardwareTypeOverviewTab state={state} />}
          {activeTab === 'config' && <ProductConfigTab hardwareType={hardwareType} canEdit={!!canEdit} onSaved={onTypeUpdated} />}
          {activeTab === 'modelrefs' && <HardwareTypeModelRefsTab state={state} canEdit={!!canEdit} />}
          {activeTab === 'devices' && (
            <div className="tab-content">
              <section className="cu-section dt-devices">
                <header className="cu-section-head">
                  <h3>{t('deviceTypes.detail.devicesTitle', { count: formatNumber(deviceCount) })}</h3>
                  <p>{t('deviceTypes.detail.devicesText', { name: hardwareType.name, code: hardwareType.code })}</p>
                  <div className="cu-section-actions">
                    <button type="button" className="btn btn-primary btn-sm" data-testid="btn-hardware-type-view-devices" onClick={() => { onClose(); navigate(`/devices?type=${hardwareType.code}`); }}>
                      {t('deviceTypes.detail.viewDevices')}
                    </button>
                  </div>
                </header>
              </section>
            </div>
          )}
          {activeTab === 'history' && <AuditHistoryTab resourceType="hardware_type" resourceId={String(hardwareType.id)} />}
        </div>

        <div className="modal-actions tpd-footer">
          {isEditing ? (
            <>
              <button type="button" className="btn btn-secondary" data-testid="btn-hardware-type-cancel" onClick={state.cancelEdit} disabled={isLoading}>
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-primary" data-testid="btn-hardware-type-save" onClick={state.save} disabled={isLoading}>
                {isLoading ? t('common.saving') : t('common.save')}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-secondary" data-testid="btn-hardware-type-close" onClick={onClose}>
                {t('common.close')}
              </button>
              {canEdit && activeTab === 'overview' && (
                <button type="button" className="btn btn-primary" data-testid="btn-hardware-type-edit" onClick={state.startEdit}>
                  {t('common.edit')}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
